import React, { useEffect } from "react";
import {
  Text,
  Grid,
  TextInput,
  PasswordInput,
  Checkbox,
  Card,
  Tooltip,
  ActionIcon,
  Divider,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconExternalLink } from "@tabler/icons";
import { useForceUpdate } from "@mantine/hooks";
import moment from "moment";

const col = { xs: 12, sm: 6, md: 3 };

const checkboxStyles = {
  label: {
    fontSize: "var(--mantine-font-size-sm)",
    fontWeight: 400,
    lineHeight: "var(--mantine-line-height-sm)",
    paddingLeft: 8,
  },
};

const HintLabel = ({ label, hint }) => (
  <Tooltip
    label={hint}
    multiline
    width={260}
    withArrow
    openDelay={200}
    position="top-start"
  >
    <Text
      span
      size="sm"
      sx={{ cursor: "help" }}
    >
      {label}
    </Text>
  </Tooltip>
);

const LoginInfoRow = ({ item }) => {
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    const isNewRow =
      !item.content.label &&
      !item.content.url &&
      !item.content.username &&
      !item.content.creationDate;
    if (isNewRow) {
      item.content.creationDate = new Date();
      forceUpdate();
    }
  }, []);

  const toDateValue = (value) => (value ? moment(value).toDate() : null);

  const setField = (field, value) => {
    item.content[field] = value;
    forceUpdate();
  };

  return (
    <Card p="sm">
      <Grid gutter="xs" mb={6} align="center">
        <Grid.Col {...col}>
          <Checkbox
            size="sm"
            styles={checkboxStyles}
            label={
              <HintLabel
                label="Consultant"
                hint="Show this login to consultants."
              />
            }
            checked={!!item.content["isPublic"]}
            onChange={(e) => setField("isPublic", e.target.checked)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <Checkbox
            size="sm"
            styles={checkboxStyles}
            label={
              <HintLabel
                label="Inactive"
                hint="Mark this login as no longer in use."
              />
            }
            checked={!!item.content["inactive"]}
            onChange={(e) => setField("inactive", e.target.checked)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <Checkbox
            size="sm"
            styles={checkboxStyles}
            label={
              <HintLabel
                label="Commission stmt"
                hint="This login can check commission statements."
              />
            }
            checked={!!item.content["checkCommissionStatement"]}
            onChange={(e) =>
              setField("checkCommissionStatement", e.target.checked)
            }
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <Checkbox
            size="sm"
            styles={checkboxStyles}
            label={
              <HintLabel
                label="Advisor login"
                hint="This login can create advisor logins."
              />
            }
            checked={!!item.content["canCreateAdvisorLogin"]}
            onChange={(e) =>
              setField("canCreateAdvisorLogin", e.target.checked)
            }
          />
        </Grid.Col>
      </Grid>

      <Divider mb="xs" />

      <Grid gutter="xs">
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="AIA Login Page"
            label={
              <HintLabel
                label="Label"
                hint="Display name for this portal login."
              />
            }
            value={item.content["label"]}
            onChange={(e) => setField("label", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="http://www.aia.com.hk"
            label={
              <HintLabel
                label="URL"
                hint="Portal login page address. Hover to open if filled."
              />
            }
            value={item.content["url"]}
            onChange={(e) => setField("url", e.target.value)}
            rightSection={
              item.content?.url ? (
                <ActionIcon
                  component="a"
                  href={item.content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                >
                  <IconExternalLink size={14} />
                </ActionIcon>
              ) : null
            }
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="Username"
            label={
              <HintLabel
                label="Login"
                hint="Username or email used to log in."
              />
            }
            value={item.content["username"]}
            onChange={(e) => setField("username", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <PasswordInput
            size="sm"
            placeholder="Password"
            label={
              <HintLabel
                label="Password"
                hint="Login password. Stored encrypted when the provider is saved."
              />
            }
            value={item.content["password"]}
            autoComplete="new-password"
            onChange={(e) => setField("password", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="PIN"
            label={
              <HintLabel
                label="PIN"
                hint="PIN or memorable name used with this portal login."
              />
            }
            value={item.content["pinMemorableName"]}
            onChange={(e) => setField("pinMemorableName", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="Role"
            label={
              <HintLabel
                label="Role"
                hint="Role of this portal login, e.g. Admin or Viewer."
              />
            }
            value={item.content["role"]}
            onChange={(e) => setField("role", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="OTP phone"
            label={
              <HintLabel
                label="OTP phone"
                hint="Phone number used for one-time password verification."
              />
            }
            value={item.content["otpPhone"]}
            onChange={(e) => setField("otpPhone", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="OTP email"
            label={
              <HintLabel
                label="OTP email"
                hint="Email address used for one-time password verification."
              />
            }
            value={item.content["otpEmail"]}
            onChange={(e) => setField("otpEmail", e.target.value)}
          />
        </Grid.Col>

        <Grid.Col {...col}>
          <PasswordInput
            size="sm"
            placeholder="Statement password"
            label={
              <HintLabel
                label="Statement PW"
                hint="Password for opening the commission statement. Stored encrypted when saved."
              />
            }
            value={item.content["statementPassword"]}
            autoComplete="new-password"
            onChange={(e) => setField("statementPassword", e.target.value)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <DatePicker
            size="sm"
            label={
              <HintLabel
                label="Created"
                hint="Date this login entry was created. Defaults to today for new rows."
              />
            }
            allowFreeInput
            value={toDateValue(item.content["creationDate"])}
            onChange={(d) => setField("creationDate", d)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <DatePicker
            size="sm"
            label={
              <HintLabel
                label="Terminated"
                hint="Optional date this login entry was terminated."
              />
            }
            allowFreeInput
            clearable
            value={toDateValue(item.content["terminationDate"])}
            onChange={(d) => setField("terminationDate", d)}
          />
        </Grid.Col>
        <Grid.Col {...col}>
          <TextInput
            size="sm"
            placeholder="Remark"
            label={
              <HintLabel
                label="Remark"
                hint="Optional notes for this portal login."
              />
            }
            value={item.content["remark"]}
            onChange={(e) => setField("remark", e.target.value)}
          />
        </Grid.Col>
      </Grid>
    </Card>
  );
};

export default LoginInfoRow;
